import { CaptureStep } from "@idw-types/data/capture-step";
import { useSelector } from "react-redux";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { getLivenessCheck, getVerificationConfiguration } from "@redux/selectors/verification-configuration-selectors";
import { useMemo, useRef, useEffect } from "react";
import { Logger } from "@utils/logger";
import { datadogRum } from "@datadog/browser-rum";
import { AuditLogLevel } from "@enums/audit-log-level";
import { validateLiveness } from "@requests/validate-liveness";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { APValidationLivenessAssessmentTypes } from "@enums/ap-validation-liveness-assessment-types";
import { APValidationTypes } from "@enums/ap-validation-types";
import { APValidationLivenessAssessmentResult } from "@idw-types/requests/ap-validation-liveness-assessment-result";
import { PrimaryButton } from "../primary-button/primary-button";
import { getCaptureStepNativeCameraButtonText } from "@utils/capture-step-util";
import { AuditLogTypes } from "@constants/audit-log-types";
import styles from "./acuant-face-capture-container.less";

type AcuantFaceCaptureContainerProps = {
    captureStep: CaptureStep;
    isCapturing: boolean;
    isProcessing: boolean;
    setShowCaptureError: (showCaptureError: boolean) => void;
    setIsCapturingAndProcessing: (isCapturing: boolean, isProcessing: boolean) => void;
    setLivenessValidationResult: (result: APValidationLivenessAssessmentResult) => void;
    setImageUrl: (imageUrl: string) => void;
};

export const AcuantFaceCaptureContainer = (props: AcuantFaceCaptureContainerProps) => {
    const {
        captureStep,
        isCapturing,
        isProcessing,
        setShowCaptureError,
        setIsCapturingAndProcessing,
        setLivenessValidationResult,
        setImageUrl
    } = props;

    const { writeAuditLog } = useAuditLogger();
    const verificationConfiguration = useSelector(getVerificationConfiguration);
    const queryParameters = useSelector(getQueryParametersState);
    const content = useSelector(getLanguageContent);
    const livenessCheck = useSelector(getLivenessCheck);

    const inputRef = useRef<HTMLInputElement>(null);
    // const [useManualCapture, setUseManualCapture] = useState(false);

    const startImageWorker = () => {
        return new Promise(resolve => {
            AcuantJavascriptWebSdk.startImageWorker(resolve);
        });
    };

    useEffect(() => {
        startImageWorker();
    }, []);

    const checkLiveness = async (base64Image: string) => {
        Logger.info(`[AcuantCameraDirective] Check Liveness with AXN APValidation`);
        try {
            writeAuditLog(AuditLogTypes.DCUI_LIVENESS_CHECK_START);
            setIsCapturingAndProcessing(false, true);
            const response = await validateLiveness(
                queryParameters,
                base64Image,
                verificationConfiguration.asi
            );
            const json = await response.json();

            let livenessResponse: APValidationLivenessAssessmentResult = {
                result: null,
                assessment: APValidationLivenessAssessmentTypes.NotLive,
                errorCode: null,
                errorMessage: null,
                redirectUrl: null,
                message: null,
                status: 200,
            };
            const { status, message, redirectUrl, validationResult } = json;
            const livenessValidationResult = validationResult[APValidationTypes.LIVENESS];
            if (livenessValidationResult) {
                livenessResponse = { ...livenessResponse, ...livenessValidationResult, message, redirectUrl, status };
            } else {
                livenessResponse = { ...livenessResponse, message, redirectUrl, status };
            }
            if (livenessResponse.result === 'pass') {
                livenessResponse.assessment = APValidationLivenessAssessmentTypes.Live;
            }
            if (livenessResponse.errorCode !== null || livenessResponse.status !== 200) {
                livenessResponse.assessment = APValidationLivenessAssessmentTypes.Error;
            }
            writeAuditLog(AuditLogTypes.DCUI_LIVENESS_CHECK_SUCCESS);
            setLivenessValidationResult(livenessResponse);
            setIsCapturingAndProcessing(false, false);
            setImageUrl(`data:image/jpeg;base64,${base64Image}`);

        } catch (error) {
            writeAuditLog(AuditLogTypes.DCUI_LIVENESS_CHECK_FAILURE, AuditLogLevel.ERROR);
            Logger.error(`[AcuantCameraDirective] Liveness capture error caught`, error);
            const dataDogerror = new Error(`[AcuantCameraDirective] Liveness capture error caught`);
            datadogRum.addError(dataDogerror, { error });
            //set error happened
            setLivenessValidationResult({
                result: null,
                assessment: APValidationLivenessAssessmentTypes.Error,
                errorCode: null,
                errorMessage: null,
                redirectUrl: null,
                message: null,
                status: 200
            });
            setIsCapturingAndProcessing(false, false);
            setImageUrl(`data:image/jpeg;base64,${base64Image}`);
        }
    }

    const faceCaptureCallback = {
        // eslint-disable-next-line
        onDetection: (_text: string) => {
            // Triggered when the face does not pass the scan. The UI element
            // should be updated here to provide guidence to the user
        },
        onOpened: () => {
            // Camera has opened
            Logger.info('[AcuantCameraDirective] Selfie Camera has been opened');
        },
        onClosed: () => {
            // Camera has closed
            Logger.info('[AcuantCameraDirective] Selfie Camera has been closed');
            setIsCapturingAndProcessing(false, false);
        },
        onError: (error: any) => {
            Logger.error('[AcuantCameraDirective] Selfie camera has an error: ', error);
        },
        onPhotoTaken: () => {
            // The photo has been taken and it's showing a preview with a button to accept or retake the image
            Logger.info('[AcuantCameraDirective] Selfie Camera Photo has been taken');
        },
        onPhotoRetake: () => {
            // Triggered when retake button is tapped
            Logger.info('[AcuantCameraDirective] Selfie Camera retake has been pressed');
        },
        onCaptured: (base64Image: string) => {
            if (base64Image) {
                setIsCapturingAndProcessing(false, true);
                // Triggered when accept button is tapped
                if (livenessCheck) {
                    checkLiveness(base64Image);
                } else {
                    Logger.info(`[AcuantCameraDirective] Don't Check Liveness with Acuant`);
                    setImageUrl(`data:image/jpeg;base64,${base64Image}`);
                    writeAuditLog(AuditLogTypes.DCUI_ACUANT_SELFIE_CAPTURE_COMPLETED);
                    setIsCapturingAndProcessing(false, false);
                }
            } else {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_CROPPING_ERROR, AuditLogLevel.ERROR);
                Logger.info('[AcuantCameraDirective] Cropping error, restart the capture');
                setShowCaptureError(true);
                setIsCapturingAndProcessing(false, false);
            }
        },
    };

    const handleChange = () => {

    };

    // const startAcuantSelfieCamera = () => {
    //     Logger.info('[AcuantCameraDirective] Start AcuantPassiveLiveness Selfie Camera');
    //     try {
    //         AcuantPassiveLiveness.start(faceCaptureCallback);
    //     } catch (error) {
    //         console.error(error);
    //         Logger.error(`[AcuantCameraDirective] Selfie capture error`, error);
    //         writeAuditLog(`DCUI_ACUANT_SELFIE_CAMERA_ERROR`, AuditLogLevel.ERROR);
    //         const dataDogerror = new Error(`[AcuantCameraDirective] Selfie capture error`);
    //         datadogRum.addError(dataDogerror, { error });
    //     }
    // };

    const startManualSelfieCamera = async () => {
        Logger.info(`[AcuantCameraDirective] Start AcuantPassiveLiveness Manual Capture`);
        try {
            AcuantPassiveLiveness.startManualCapture(faceCaptureCallback.onCaptured);
        } catch (error) {
            Logger.error(`[AcuantCameraDirective] Manual capture error`, error);
            const dataDogerror = new Error(`[AcuantCameraDirective] Manual capture error`);
            datadogRum.addError(dataDogerror, { error });
        }
    };

    const onClickCapture = () => {
        if (inputRef.current) {
            inputRef.current.value = null;
        }
        setIsCapturingAndProcessing(true, false);
        writeAuditLog(AuditLogTypes.DCUI_CAPTURE_BUTTON_HIT);
        if (typeof AcuantPassiveLiveness !== "undefined") {
            // if (AcuantCamera.isCameraSupported) {
            //     writeAuditLog(AuditLogTypes.DCUI_ACUANT_SELFIE_CAMERA_STARTED);
            //     startAcuantSelfieCamera();
            // } else {
            writeAuditLog(AuditLogTypes.DCUI_ACUANT_SELFIE_CAMERA_STARTED);
            startManualSelfieCamera();
            // }
        } else {
            Logger.info('[AcuantCameraDirective] No AcuantPassiveLiveness available!');
        }
    };

    const buttonText = useMemo(() => {
        return getCaptureStepNativeCameraButtonText(captureStep.type, content);
    }, [captureStep.type, content]);

    return (
        <div className={styles.acuantFaceCaptureContainer}>
            <input
                ref={inputRef}
                className={styles.fileInput}
                type="file"
                accept="image/*"
                onChange={handleChange}
                id={"capture-input"}
            />
            <div id="acuant-face-capture-container" />
            {!isCapturing && !isProcessing && (
                <div className={styles.buttonContainer}>
                    <PrimaryButton
                        disabled={false}
                        label={buttonText}
                        type={"button"}
                        automationId={"start-capture"}
                        id={"start-capture"}
                        hideSpinner={true}
                        onClick={onClickCapture}
                    />
                </div>
            )}
        </div>
    );
};